/**
 * FileDropzone — drag-n-drop + click-to-pick зона.
 *
 * - Валидация по расширению (xlsx, xls, csv, docx, pdf).
 * - Валидация по размеру (maxMb из props, default FILE_MAX_SIZE_MB).
 * - onFile(file) — единственный файл за раз.
 *
 * Phase 1.3 (Files UX Refactor 2026-08-06): default лимит теперь из
 * lib/constants.ts (200 MB), а не magic 50. Подсказка про размер —
 * через t('files.upload.max_size', { mb: maxMb }) — была hardcoded English
 * «max {maxMb} MB», теперь локализуется.
 */
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';

import { cn } from '@/lib/utils';
import { FILE_MAX_SIZE_MB } from '@/lib/constants';

interface FileDropzoneProps {
  onFile: (file: File) => void;
  maxMb?: number;
  disabled?: boolean;
  className?: string;
}

export function FileDropzone({
  onFile,
  maxMb = FILE_MAX_SIZE_MB,
  disabled = false,
  className,
}: FileDropzoneProps) {
  const { t } = useTranslation();

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop: (accepted) => {
        if (accepted[0]) onFile(accepted[0]);
      },
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls'],
        'text/csv': ['.csv'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/pdf': ['.pdf'],
      },
      maxFiles: 1,
      maxSize: maxMb * 1024 * 1024,
      disabled,
    });

  const firstError = fileRejections[0]?.errors[0];

  return (
    <div className="space-y-2">
      <div
        {...getRootProps({
          className: cn(
            'card flex flex-col items-center justify-center py-12 px-6 cursor-pointer transition-colors border-2 border-dashed',
            isDragActive
              ? 'border-primary-500 bg-primary-500/5'
              : 'border-surface-700 hover:border-surface-600',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          ),
        })}
      >
        <input {...getInputProps()} />
        <Upload className="w-10 h-10 text-surface-500 mb-3" />
        <p className="text-sm font-medium text-surface-200">
          {t('files.upload.dropzone')}
        </p>
        <p className="text-xs text-surface-500 mt-1">
          {t('files.upload.dropzone_hint')} ·{' '}
          {t('files.upload.max_size', { mb: maxMb, defaultValue: `До ${maxMb} МБ` })}
        </p>
      </div>
      {firstError && (
        <p className="text-xs text-danger">
          {firstError.code === 'file-too-large'
            ? t('files.upload.errors.too_large')
            : firstError.code === 'file-invalid-type'
              ? t('files.upload.errors.wrong_type')
              : firstError.message}
        </p>
      )}
    </div>
  );
}
